/**
 * The CEO's desk (owner, 26 Oct 2026: "create one more login for owner head
 * admin panel is confusing for owner he wants something personal he wants
 * numbers and facts and approval and task his main work is analysis create a
 * user SUNNY CEO"). One sign-in, one page of the whole business, and a chat
 * that answers questions from the same figures.
 *
 * The desk is one of the owner's head-admin desks (HEAD_ADMIN_DESK_EMAILS):
 * it answers approvals and gives tasks as the owner, and is never counted as
 * an employee. Every figure on it is read by the function that already counts
 * it elsewhere in the app, so the CEO never sees a number that disagrees with
 * the page it came from.
 */
import { isHeadAdminDesk } from './domain.js';

/** Sunny's own door. Listed with the other head-admin desks in domain.ts. */
export const CEO_DESK_EMAIL = 'ceo@brightautozone.in';

export function isCeoDesk(user: { email: string } | null | undefined): boolean {
  return Boolean(user) && user!.email.trim().toLowerCase() === CEO_DESK_EMAIL;
}

/** Who reads the CEO's page and asks its chat: the owner's desks, the CEO's among them. */
export function readsCeoDesk(user: { email: string } | null | undefined): boolean {
  return isHeadAdminDesk(user);
}

/** The retail books, each counted on its own; Bright Autozone is counted at its gate instead. */
export const CEO_RETAIL_BOOKS = ['bow', 'fabev', 'tvs'] as const;
export type CeoRetailBook = (typeof CEO_RETAIL_BOOKS)[number];

export interface CeoBookSales {
  book: CeoRetailBook;
  name: string;
  today: number;
  thisMonth: number;
  prevMonth: number;
  thisFy: number;
  byChannel: { mitr: number; outlet: number; direct: number; dealer: number };
}

/** One place vehicles stand, and how many there are past the 60-day loss line where that is counted. */
export interface CeoStockPlace {
  key: string;
  label: string;
  /** Whose and where, in a few words. */
  where: string;
  count: number;
  over60: number | null;
}

export interface CeoAsmRow {
  userId: string;
  name: string;
  book: string;
  territory: string | null;
  meetings: number;
  /** Logged at the place, with the location and the selfie. */
  meetingsProved: number;
  /** Enquiries the ASM put in this month. */
  enquiries: number;
  converted: number;
  lost: number;
  orders: number;
  vehiclesOrdered: number;
  /** Their counters' sales this month and last; null where the sign-in answers for no ASM record. */
  sold: number | null;
  soldLastMonth: number | null;
  inHand: number | null;
  over60: number | null;
  expenses: number;
}

export type CeoCounterKind = 'MITR' | 'OUTLET' | 'DEALER';

export interface CeoCounterRow {
  id: string;
  name: string;
  area: string | null;
  kind: CeoCounterKind;
  book: string;
  soldThisMonth: number;
  soldLastMonth: number;
  soldFy: number;
  /** Vehicles received this month — what a dealer bought. */
  receivedThisMonth: number;
  receivedFy: number;
  inHand: number;
  over60: number;
  lastSaleDay: string | null;
  daysSinceLastSale: number | null;
}

/** Stock standing at a counter with no sale in this many days: the CEO's "not performing". */
export const CEO_IDLE_DAYS = 45;

export interface CeoOverview {
  /** When the figures were read. */
  asOf: string;
  /** Today and this month, IST. */
  day: string;
  month: string;
  dayOfMonth: number;
  daysInMonth: number;
  pulse: {
    soldToday: number;
    soldThisMonth: number;
    soldPrevMonth: number;
    dispatchedThisMonth: number;
    dispatchedPrevMonth: number;
    vehiclesOwed: number;
    stock: number;
  };
  sales: {
    books: CeoBookSales[];
    /** Oldest first: each book's sales in the month, IST. */
    months: Array<{ month: string } & Record<CeoRetailBook, number>>;
    /** Out of Bright Autozone's gate: vehicles, a vehicle once a month. */
    gate: { thisMonth: number; prevMonth: number; thisFy: number };
  };
  stock: { total: number; places: CeoStockPlace[] };
  orders: {
    open: number;
    vehiclesOwed: number;
    waitingApproval: number;
    vehiclesDispatchedWeek: number;
    byAsm: Array<{ name: string; openOrders: number; toGo: number }>;
  };
  asms: CeoAsmRow[];
  network: {
    counters: number;
    selling: number;
    idle: number;
    top: CeoCounterRow[];
    idleList: CeoCounterRow[];
    dealers: CeoCounterRow[];
  };
  people: {
    day: string;
    calendar: 'HOLIDAY' | 'WEEK_OFF' | null;
    holiday: string | null;
    people: number;
    present: number;
    late: number;
    onLeave: number;
    absent: number;
    notMarked: number;
    half: number;
    /** Who is late or not in yet, by name — the few the CEO would ring. */
    lateNames: string[];
    missingNames: string[];
  };
  money: {
    dayBook: Array<{ book: string; name: string; paid: number; received: number; closing: number | null; heads: Array<{ label: string; amount: number }> }>;
    expenses: { total: number; approved: number; pending: number; pendingCount: number };
  };
  work: {
    approvals: number;
    tasks: { open: number; overdue: number; dueToday: number; waiting: number };
  };
}

/** The chat (owner: "he will get a chatbot for all his qureis … we will use opus 5.5"). */
export interface CeoChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** What the server streams back, one JSON object per line. */
export type CeoAskEvent =
  | { type: 'text'; text: string }
  | { type: 'reading'; label: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

/** How much of the conversation is sent back with each question. */
export const CEO_CHAT_KEEP = 24;
/** The longest earlier turn sent back; a longer one is sent shortened. */
export const CEO_TURN_MAX = 20_000;
/** Where the web keeps a tab's conversation — cleared whenever a session ends. */
export const CEO_CHAT_STORE_KEY = 'bsa.ceo.chat';
export const CEO_QUESTION_MAX = 2000;
